import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zaloBotTokenSchema, type EventDetailDto, type EventZaloBotDto, type ZaloNotifySettings } from '@rw/shared';
import { useState, type ReactNode } from 'react';
import { Button, Card } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { toast } from '../../stores/toasts';
import { Toggle } from '../profile/SettingsCard';
import { useT } from '../../i18n';
import { saveZaloBot } from './zalo-api';

const input = 'h-11 w-full rounded-xl border border-ink-700 bg-ink-900 px-3 text-sm outline-none focus:border-volt';
const zaloKey = (id: string) => ['event', id, 'zalo'] as const;

/** Kinds of group announcements the organisers can switch off. */
const NOTIFY_KINDS: Array<{ key: keyof ZaloNotifySettings; label: string; description: string }> = [
  { key: 'start', label: 'Event start', description: 'Once, when runs start counting, with the rules.' },
  { key: 'results', label: 'Results', description: 'The winners when the event ends, or a cancellation.' },
  { key: 'teams', label: 'New teams', description: 'Each team the organisers add.' },
  { key: 'joins', label: 'Runners joining', description: 'Each runner who joins or changes team.' },
  { key: 'runs', label: 'Every counted run', description: 'One message per run — the busiest kind.' },
];

/** Organiser tools: a Zalo bot that announces this event in the runners' Zalo group. */
export function ZaloBotCard({ e }: { e: EventDetailDto }) {
  const t = useT();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: zaloKey(e.id),
    queryFn: async () => (await api<EventZaloBotDto | null>(`/competitions/${e.id}/zalo`)) || null,
    // Waiting for someone to @mention the bot in the group: notice it without a reload.
    refetchInterval: (query) => (query.state.data && !query.state.data.groupLinked ? 5000 : false),
  });
  const [token, setToken] = useState('');
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const done = (bot: EventZaloBotDto | null, title?: string) => {
    qc.setQueryData(zaloKey(e.id), bot);
    if (title) toast({ kind: 'success', title: t(title) });
  };
  const fail = (e: Error) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : t('Something went wrong') });

  const save = useMutation({
    mutationFn: (v: string) => saveZaloBot(e.id, v),
    onSuccess: (bot) => (done(bot, 'Zalo bot saved'), setToken(''), setEditing(false), setError(null)),
    onError: (err) => setError(err instanceof ApiError ? err.message : t('Something went wrong')),
  });
  const remove = useMutation({ mutationFn: () => api(`/competitions/${e.id}/zalo`, { method: 'DELETE' }), onSuccess: () => done(null, 'Zalo bot removed'), onError: fail });
  const unlink = useMutation({ mutationFn: () => api<EventZaloBotDto>(`/competitions/${e.id}/zalo/unlink-group`, { method: 'POST' }), onSuccess: (b) => done(b, 'Group disconnected'), onError: fail });
  const notify = useMutation({
    mutationFn: (v: Partial<ZaloNotifySettings>) => api<EventZaloBotDto>(`/competitions/${e.id}/zalo/notify`, { method: 'PATCH', body: v }),
    onSuccess: (b) => done(b),
    onError: fail,
  });
  const announce = useMutation({ mutationFn: () => api<EventZaloBotDto>(`/competitions/${e.id}/zalo/announce`, { method: 'POST' }), onSuccess: (b) => done(b, 'Posted to the Zalo group'), onError: fail });

  const submit = () => {
    const parsed = zaloBotTokenSchema.safeParse({ token });
    if (!parsed.success) return setError(t(parsed.error.issues[0]?.message ?? 'Invalid'));
    save.mutate(parsed.data.token);
  };
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ kind: 'success', title: t('Copied') });
    } catch {
      /* clipboard blocked */
    }
  };

  const bot = q.data;
  const tokenForm = (
    <div className="space-y-2">
      <input
        value={token}
        onChange={(ev) => setToken(ev.target.value)}
        placeholder={t('Bot token from Zalo Bot Creator')}
        autoComplete="off"
        spellCheck={false}
        className={input}
        data-testid="zalo-token-input"
      />
      {error ? <p className="text-xs text-enemy">{error}</p> : null}
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} loading={save.isPending} disabled={!token.trim()} data-testid="zalo-save">
          {t('Save bot')}
        </Button>
        {bot ? (
          <Button size="sm" variant="ghost" onClick={() => (setEditing(false), setError(null))}>
            {t('Cancel')}
          </Button>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="mt-3" data-testid="zalo-card">
    <Card className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">{t('Zalo group announcements')}</div>
        {bot ? <span className={`text-[11px] font-bold uppercase tracking-wider ${bot.groupLinked ? 'text-volt' : 'text-gold'}`}>{t(bot.groupLinked ? 'Connected' : 'Waiting for group')}</span> : null}
      </div>
      {q.isPending ? null : !bot ? (
        <>
          <p className="text-xs text-ink-400">
            {t('A Zalo bot posts the event, new teams, the start, every counted run and the winners to your runners’ Zalo group. Create a bot in the Zalo Bot Creator app, then paste its token here.')}{' '}
            <a href="https://bot.zaloplatforms.com/docs/create-bot/" target="_blank" rel="noreferrer" className="font-semibold text-volt">
              {t('How?')}
            </a>
          </p>
          {tokenForm}
        </>
      ) : (
        <>
          <div className="text-xs text-ink-400">
            {t('Bot')} <b className="text-ink-100">{bot.botName}</b> · {t('token …{hint}', { hint: bot.tokenHint })}
          </div>

          <ol className="space-y-3 text-sm">
            <Step n={1} done={bot.webhookOk} title={t(bot.webhookOk ? 'Webhook set on the bot automatically' : 'Set the webhook on your bot')}>
              {!bot.webhookOk ? <p className="text-xs text-ink-400">{t('Zalo did not accept it automatically. Set it yourself (setWebhook) with this URL and secret token.')}</p> : null}
              <CopyRow label={t('Webhook URL')} value={bot.webhookUrl} onCopy={copy} testId="zalo-webhook-url" />
              <CopyRow label={t('Secret token')} value={bot.secretToken} onCopy={copy} testId="zalo-secret" />
            </Step>
            <Step n={2} done={bot.groupLinked} title={t('Share the bot’s link in your runners’ Zalo group')}>
              {!bot.canJoinGroups ? <p className="text-xs text-gold">{t('This bot is not allowed to join groups yet — turn that on in Zalo Bot Creator.')}</p> : null}
              <p className="text-xs text-ink-400">{t('Use the group invite link Zalo gave you when the bot was created, and confirm “Add bot to group”. The group is connected as soon as Zalo tells us about it.')}</p>
            </Step>
            <Step n={3} done={bot.groupLinked} title={t(bot.groupLinked ? 'Group connected' : 'Not connected yet? @mention the bot once')}>
              <p className="text-xs text-ink-400">
                {bot.groupLinked
                  ? t('Announcements go to this group. The bot never replies to messages.')
                  : t('If the group did not get the event introduction, type @, pick the bot and send anything. The first group the bot hears from gets the announcements.')}
              </p>
            </Step>
          </ol>

          {bot.notify ? (
            <div className="space-y-3 border-t border-ink-700 pt-3" data-testid="zalo-notify">
              <div>
                <div className="text-sm font-semibold">{t('What the bot posts')}</div>
                <p className="text-xs text-ink-400">{t('Switch off what your group does not need. The introduction and “Post event again” always go out.')}</p>
              </div>
              {NOTIFY_KINDS.map((k) => (
                <Toggle
                  key={k.key}
                  id={`zalo-notify-${k.key}`}
                  checked={notify.isPending && notify.variables[k.key] !== undefined ? !!notify.variables[k.key] : bot.notify[k.key]}
                  disabled={notify.isPending}
                  onChange={(v) => notify.mutate({ [k.key]: v })}
                  label={t(k.label)}
                  description={t(k.description)}
                />
              ))}
            </div>
          ) : null}

          {bot.lastError ? (
            <p className="rounded-lg bg-enemy/10 px-3 py-2 text-xs text-enemy" data-testid="zalo-error">
              {t('Last message failed: {error}', { error: bot.lastError })}
            </p>
          ) : null}

          {editing ? (
            tokenForm
          ) : (
            <div className="flex flex-wrap gap-2">
              {bot.groupLinked ? (
                <>
                  <Button size="sm" variant="secondary" onClick={() => announce.mutate()} loading={announce.isPending} data-testid="zalo-announce">
                    {t('Post event again')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => unlink.mutate()} disabled={unlink.isPending}>
                    {t('Disconnect group')}
                  </Button>
                </>
              ) : null}
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
                {t('Change token')}
              </Button>
              <Button size="sm" variant="ghost" className="text-enemy" onClick={() => remove.mutate()} disabled={remove.isPending} data-testid="zalo-remove">
                {t('Remove bot')}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
    </div>
  );
}

function Step({ n, done, title, children }: { n: number; done: boolean; title: string; children?: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className={`grid size-6 shrink-0 place-items-center rounded-full text-xs font-bold ${done ? 'bg-volt text-ink-950' : 'bg-ink-800 text-ink-300'}`}>{done ? '✓' : n}</span>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="font-semibold">{title}</div>
        {children}
      </div>
    </li>
  );
}

function CopyRow({ label, value, onCopy, testId }: { label: string; value: string; onCopy: (v: string) => void; testId: string }) {
  const t = useT();
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-ink-500">{label}</div>
      <div className="mt-0.5 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-lg bg-ink-900 px-2 py-1.5 text-xs text-ink-300" data-testid={testId}>
          {value}
        </code>
        <Button size="sm" variant="secondary" onClick={() => onCopy(value)}>
          {t('Copy')}
        </Button>
      </div>
    </div>
  );
}
